import { format, isValid, parseISO } from 'date-fns';

/**
 * Formats a date string or Date to DD/MM/YYYY using UTC
 */
export const formatDate = (input: string | Date | undefined): string => {
  if (!input) return '-';
  const date = typeof input === 'string' ? parseISO(input) : input;
  if (!isValid(date)) return '-';
  const utcDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  return format(utcDate, 'dd/MM/yyyy');
};

/**
 * Pins a user-picked calendar day to UTC midnight.
 *
 * Date pickers produce a `Date` at *local* midnight. The generated client serializes
 * date-only fields with `value.toISOString().substring(0, 10)` (UTC), so a local-midnight
 * date submitted from a timezone west of UTC serializes to the *previous* calendar day.
 * Reading the local Y/M/D and rebuilding them at UTC midnight keeps the picked day intact.
 *
 * Use for any `Date` headed to a date-only (`format: date`) backend field — e.g. ServiceRole
 * `validFrom`/`validTo`, ViDetails `dateOfBirth`.
 */
export const toUtcCalendarDate = (input: Date | undefined): Date | undefined => {
  if (!input || !isValid(input)) return undefined;
  return new Date(Date.UTC(input.getFullYear(), input.getMonth(), input.getDate()));
};

/**
 * Formats an already-normalized wall-clock date time string without applying any timezone shift.
 * Intended for backend values that should be displayed exactly as delivered.
 */
export const formatWallClockDateTime = (input: string | Date | undefined): string => {
  if (!input) return '-';

  if (typeof input === 'string') {
    const match = input.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (match) {
      const [, year, month, day, hours, minutes] = match;
      return `${day}/${month}/${year} - ${hours}:${minutes}`;
    }

    const parsed = parseISO(input);
    if (!isValid(parsed)) return '-';
    return format(parsed, 'dd/MM/yyyy - HH:mm');
  }

  if (!isValid(input)) return '-';
  return format(input, 'dd/MM/yyyy - HH:mm');
};

/**
 * Formats the calendar-day part of an already-normalized wall-clock date time string without
 * applying any timezone shift. Use to group values that share the same day.
 */
export const formatWallClockDate = (input: string | Date | undefined): string => {
  if (!input) return '-';

  if (typeof input === 'string') {
    const match = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      return `${day}/${month}/${year}`;
    }

    const parsed = parseISO(input);
    if (!isValid(parsed)) return '-';
    return format(parsed, 'dd/MM/yyyy');
  }

  if (!isValid(input)) return '-';
  return format(input, 'dd/MM/yyyy');
};

/**
 * Formats the calendar-day part of an already-normalized wall-clock date time string without
 * applying any timezone shift. Use to group values that share the same day.
 * showing in format: dd.MM.yyyyy e.g: 24.09.2026
 */
export const formatRevisionHistoryClockDate = (input: string | Date | undefined): string => {
  if (!input) return '-';

  if (typeof input === 'string') {
    const match = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      return `${day}.${month}.${year}`;
    }

    const parsed = parseISO(input);
    if (!isValid(parsed)) return '-';
    return format(parsed, 'dd.MM.yyyy');
  }

  if (!isValid(input)) return '-';
  return format(input, 'dd.MM.yyyy');
};

/**
 * Formats an already-normalized wall-clock date time string without applying any timezone shift with AM/PM.
 *
 */
export const formatWallClockDateTimeAMPM = (input: string | Date | undefined): string => {
  if (!input) return '-';

  if (typeof input === 'string') {
    const match = input.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (match) {
      const [, year, month, day, hours, minutes] = match;
      return `${day}/${month}/${year} - ${hours}:${minutes}`;
    }
    const parsed = parseISO(input);
    if (!isValid(parsed)) return '-';
    return format(parsed, 'dd.MM.yyyy | HH:mm a');
  }

  if (!isValid(input)) return '-';
  return format(input, 'dd.MM.yyyy | HH:mm a');
};
